/**
 * Creates an accordion style toggle.
 * Provide a parent element that contains the accordion.
 * Define the heading elements and body elements with heading and body classes.
 * Assign the headings and bodies matching id via the data-id attribute.
 */
export class AccordionToggle {
    /**
     * @var HTMLElement parent_element the container for the accordion.
     * @var NodeList headings visible that will expand when clicked.
     * @var NodeList bodies that will display when their parent is clicked.
     * @var {boolean} clickable flag for whether user can select tabs by clicking (default true)
     */
    parent_element: HTMLElement;
    headings: NodeListOf<HTMLElement>;
    bodies: NodeListOf<HTMLElement> ;
    active_id: string;
    clickable: boolean;

    /**
     * Setup the class from the parent element. Add click methods to the headings and display the first one.
     *
     * @param {HTMLElement} element parent container to build the accordion from.
     * @returns {AccordionToggle} setup and ready.
     */
    constructor(element:HTMLElement, clickable:boolean = true) {
        this.parent_element = element;
        this.headings = element.querySelectorAll('.tv-accordion-toggle__heading');
        this.bodies = element.querySelectorAll('.tv-accordion-toggle__body');
        this.active_id = '';
        this.clickable = clickable;

        for (const heading of this.headings) {
            heading.addEventListener('click', this.heading_clicked.bind(this));
        }

        this.toggle(this.headings[0]);
    }

    /**
     * Expand or collapse accordion based on the passed heading id
     *
     * @param {string} id of the heading to expand
     * @returns {void}
     */
    toggle_id(id:string): void {
        for (const heading of this.headings) {
            if (heading.dataset.id === id) {
                this.toggle(heading);
                break;
            }
        }
    }

    /**
     * Event to handle a heading click. Pass the target to the toggle function.
     *
     * @param {Event} e from the click.
     * @returns {void}
     */
    heading_clicked(e) : void {
        if (e.isTrusted && this.clickable) {
            this.toggle(e.currentTarget);
        }
    }

    /**
     * Expand or collapse the accordion based on the passed element.
     * If no elements are passed, all accordion bodies are collapsed.
     *
     * @param {HTMLElement} heading to expand.
     * @returns {void}
     */
    toggle(heading:HTMLElement = null) : void {
        if (heading && heading.dataset.id === this.active_id) {
            return;
        }

        this.active_id = heading ? heading.dataset.id : '';

        for (const body of this.bodies) {
            if (!body.classList.contains('hidden')) {
                body.classList.add('hidden');
            }

            if (heading && body.dataset.id === heading.dataset.id) {
                body.classList.remove('hidden');
            }
        }

        // Add 'selected' class to immediate parent
        for (const heading_el of this.headings) {
            if (heading_el.dataset.id === this.active_id) {
                heading_el.parentElement.classList.add('selected');
            } else {
                heading_el.parentElement.classList.remove('selected');
            }
        }
    }
}
